import { db } from '../firebase';
import { collection, query, where, getDocs, limit, orderBy } from 'firebase/firestore';

export interface UserSearchResult {
  userId: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  photoURL?: string;
  companyName?: string;
}

/**
 * Search for users by name or email
 * @param searchTerm - The search term (minimum 2 characters)
 * @param maxResults - Maximum number of results (default: 10)
 * @returns Array of matching users
 */
export const searchUsers = async (
  searchTerm: string,
  maxResults: number = 10
): Promise<UserSearchResult[]> => {
  if (!searchTerm || searchTerm.length < 2) {
    return [];
  }

  try {
    const searchLower = searchTerm.toLowerCase().trim();
    const usersRef = collection(db, 'userProfiles');
    
    // Firestore doesn't support full-text search, so we'll do a prefix search
    // on displayName and firstName fields
    const results: UserSearchResult[] = [];
    const seenIds = new Set<string>();

    // Search by displayName (prefix match)
    const displayNameQuery = query(
      usersRef,
      where('displayName', '>=', searchTerm),
      where('displayName', '<=', searchTerm + '\uf8ff'),
      limit(maxResults),
      orderBy('displayName')
    );

    // Search by firstName (prefix match)
    const firstNameQuery = query(
      usersRef,
      where('firstName', '>=', searchTerm),
      where('firstName', '<=', searchTerm + '\uf8ff'),
      limit(maxResults),
      orderBy('firstName')
    );

    // Search by lastName (prefix match)
    const lastNameQuery = query(
      usersRef,
      where('lastName', '>=', searchTerm),
      where('lastName', '<=', searchTerm + '\uf8ff'),
      limit(maxResults),
      orderBy('lastName')
    );

    // Search by companyName (prefix match)
    const companyNameQuery = query(
      usersRef,
      where('companyName', '>=', searchTerm),
      where('companyName', '<=', searchTerm + '\uf8ff'),
      limit(maxResults),
      orderBy('companyName')
    );

    const [displayNameSnap, firstNameSnap, lastNameSnap, companyNameSnap] = await Promise.all([
      getDocs(displayNameQuery).catch(() => ({ docs: [] })),
      getDocs(firstNameQuery).catch(() => ({ docs: [] })),
      getDocs(lastNameQuery).catch(() => ({ docs: [] })),
      getDocs(companyNameQuery).catch(() => ({ docs: [] }))
    ]);

    // Combine results and filter duplicates
    const allDocs = [
      ...displayNameSnap.docs,
      ...firstNameSnap.docs,
      ...lastNameSnap.docs,
      ...companyNameSnap.docs
    ];

    for (const docSnap of allDocs) {
      const data = docSnap.data();
      const userId = docSnap.id;

      if (seenIds.has(userId)) continue;
      seenIds.add(userId);

      const displayName = data.displayName || data.firstName || data.email || 'Onbekend';
      const searchableText = `${displayName} ${data.firstName || ''} ${data.lastName || ''} ${data.companyName || ''} ${data.email || ''}`.toLowerCase();

      // Only include if search term matches (case-insensitive)
      if (searchableText.includes(searchLower)) {
        results.push({
          userId,
          displayName,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          photoURL: data.photoURL,
          companyName: data.companyName
        });
      }

      if (results.length >= maxResults) break;
    }

    // Sort by relevance (exact matches first, then prefix matches)
    results.sort((a, b) => {
      const aName = (a.displayName || '').toLowerCase();
      const bName = (b.displayName || '').toLowerCase();
      
      if (aName.startsWith(searchLower) && !bName.startsWith(searchLower)) return -1;
      if (!aName.startsWith(searchLower) && bName.startsWith(searchLower)) return 1;
      return aName.localeCompare(bName);
    });

    return results.slice(0, maxResults);
  } catch (error) {
    console.error('Error searching users:', error);
    return [];
  }
};

/**
 * Extract @mentions from text
 * @param text - The text to parse
 * @returns Array of mentioned usernames (without @)
 */
export const extractMentions = (text: string): string[] => {
  const mentionRegex = /@(\w+)/g;
  const matches = text.match(mentionRegex);
  if (!matches) return [];
  
  return matches.map(match => match.substring(1)); // Remove @
};

/**
 * Parse @mentions and return user IDs and names
 * @param text - The text containing @mentions
 * @param mentionMap - Map of username -> userId (from autocomplete)
 * @returns Object with mentionedUserIds and mentionedUserNames arrays
 */
export const parseMentions = (
  text: string,
  mentionMap: Map<string, { userId: string; displayName: string }>
): { mentionedUserIds: string[]; mentionedUserNames: string[] } => {
  const mentionedUserIds: string[] = [];
  const mentionedUserNames: string[] = [];
  const seenIds = new Set<string>();

  const mentionRegex = /@(\w+)/g;
  let match;
  
  while ((match = mentionRegex.exec(text)) !== null) {
    const username = match[1];
    const mentionInfo = mentionMap.get(username);
    
    if (mentionInfo && !seenIds.has(mentionInfo.userId)) {
      mentionedUserIds.push(mentionInfo.userId);
      mentionedUserNames.push(mentionInfo.displayName);
      seenIds.add(mentionInfo.userId);
    }
  }

  return { mentionedUserIds, mentionedUserNames };
};


